import {
    LICENSE_USER_ACTIVE_STATUS,
    LICENSE_USER_TO_REASSIGN_STATUS,
} from "@/domain/entities/organization/OrganizationLicense";
import type OrganizationLicense from "@/domain/entities/organization/OrganizationLicense";

export type LicenseTier = "starter" | "premium" | "ultimate" | "other";

export type LicenseRecapItem = {
    available: number;
    displayName: string;
    id: string;
    tier: LicenseTier;
    toReassign: number;
    total: number;
    used: number;
};

const TIER_SORT_ORDER: Record<LicenseTier, number> = {
    starter: 0,
    premium: 1,
    ultimate: 2,
    other: 99,
};

const TIER_DISPLAY_NAMES: Record<Exclude<LicenseTier, "other">, string> = {
    starter: "business Starter",
    premium: "business Premium",
    ultimate: "business Ultimate",
};

export const resolveLicenseTier = (
    license: OrganizationLicense,
): LicenseTier => {
    const label = `${license.name ?? ""} ${license.licenseId}`.toLowerCase();

    if (label.includes("starter")) return "starter";
    if (label.includes("premium")) return "premium";
    if (label.includes("ultimate")) return "ultimate";

    return "other";
};

const getDisplayName = (
    license: OrganizationLicense,
    tier: LicenseTier,
): string => {
    if (tier !== "other") {
        return TIER_DISPLAY_NAMES[tier];
    }

    const rawName = license.name ?? license.licenseId;
    const match = rawName.match(/business\s+(\w+)/i);

    return match ? `business ${match[1]}` : rawName;
};

export const buildLicensesRecap = (
    licenses: OrganizationLicense[] | undefined,
): LicenseRecapItem[] => {
    if (!licenses?.length) return [];

    return licenses
        .map((license) => {
            const used = license.users.filter(
                (u) =>
                    u.status.toLowerCase() === LICENSE_USER_ACTIVE_STATUS &&
                    u.mail !== null,
            ).length;
            const toReassign = license.users.filter(
                (u) =>
                    u.status.toLowerCase() === LICENSE_USER_TO_REASSIGN_STATUS,
            ).length;
            const tier = resolveLicenseTier(license);

            return {
                available: license.licenseCount - used - toReassign,
                displayName: getDisplayName(license, tier),
                id: license.id,
                tier,
                toReassign,
                total: license.licenseCount,
                used,
            };
        })
        .sort(
            (a, b) =>
                TIER_SORT_ORDER[a.tier] - TIER_SORT_ORDER[b.tier] ||
                a.displayName.localeCompare(b.displayName),
        );
};
